import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ScrollToHash from './components/utils/ScrollToHash';
import FloatingSetupButton from './components/layout/FloatingSetupButton';

// Pages
import ChoosePath from './pages/ChoosePath';
import QuickStart from './pages/QuickStart';
import GbpOptimization from './pages/GbpOptimization';
import LocalDominate from './pages/LocalDominate';
import LocalAgentsPage from './pages/LocalAgentsPage';
import LocalAgentDetail from './pages/LocalAgentDetail';
import LocalOverview from './pages/LocalOverview';
import LocalDemo from './pages/LocalDemo';
import LocalReport from './pages/LocalReport';
import ReportThankYou from './pages/ReportThankYou';
import LocalProduct from './pages/LocalProduct';
import { getLocalProduct } from './config/localProducts';
import PartnerApply from './pages/PartnerApply';
import NotFound from './pages/NotFound';
import AgencyHowItWorks from './pages/AgencyHowItWorks';
import AgencyResources from './pages/AgencyResources';
import Blog from './pages/Blog';
import PricingPage from './pages/PricingPage';
import Download from './pages/Download';
import PlaygroundFeatures from './pages/PlaygroundFeatures';
import LocalTrustEngine from './pages/LocalTrustEngine';
import VibeCode from './pages/VibeCode';
import StartSetup from './pages/StartSetup';
import CaseStudiesPage from './pages/CaseStudiesPage';
import RepUpgradeInAction from './pages/RepUpgradeInAction';
import ActionPlanner from './pages/ActionPlanner';
import Agencies from './pages/Agencies';
import PartnerFit from './pages/PartnerFit';
import PartnerOnboarding from './pages/PartnerOnboarding';
import PartnerAccess from './pages/PartnerAccess';
import PartnerDiscovery from './pages/PartnerDiscovery';
import CertifiedPartner from './pages/CertifiedPartner';
import WhiteLabelPartner from './pages/WhiteLabelPartner';
import AgencyAgentDetail from './pages/AgencyAgentDetail';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import DataProcessingAgreement from './pages/DataProcessingAgreement';

// Agent Detail Pages
import { getAgencyAgent } from './config/agencyAgents';

const LEGACY_CTA_LABELS = ['Get Started', 'Choose Your Path', 'Get Upgraded'];
const PRIMARY_CTA_PATH = '/create-access';

function RouteMetaManager() {
  const location = useLocation();

  useEffect(() => {
    let title = 'Let Your Best Customers Speak for You | RepUpgrade';
    let description = 'Human-led AI for local businesses, coaches, and consultants. Turn customer feedback into review replies, useful articles, and social content in your voice.';

    if (location.pathname === '/gbp-optimization') {
      title = 'GBP Optimization + Included RepUpgrade Months | RepUpgrade';
      description = 'Purchase Google Business Profile optimization for $299. Get three months of Complete with a six-month commitment, or two months of Amplify included.';
    } else if (location.pathname === '/report-thank-you') {
      title = 'Your Next Step After the Report | RepUpgrade';
      description = 'Check your report email, explore the workflow, and choose your next step with RepUpgrade.';
    } else if (location.pathname.startsWith('/plans/')) {
      const product = getLocalProduct(location.pathname.split('/')[2]);
      title = product ? `${product.plan.name} — ${product.eyebrow} | RepUpgrade Local Dominate` : 'Plan Not Found | RepUpgrade';
      description = product ? product.intro : 'Explore Local Dominate Free, Grow, Amplify, and Complete for your own business.';
    } else if (location.pathname === '/agencies') {
      title = 'Agency Partner Dominate | RepUpgrade';
      description = 'Build a recurring authority service for local-business clients. $299/month, or $99/month with five qualifying active clients.';
    } else if (location.pathname === '/partner-apply') {
      title = 'Apply for Agency Partner Dominate | RepUpgrade';
      description = 'Prepare your five-client opportunity and book a partner qualification review with Joe Moore.';
    } else if (location.pathname === '/ai-team') {
      title = 'Meet Your Local Dominate AI Team | RepUpgrade';
      description = 'Meet RepUpgrade.ai, Ruby Replies, Mabel Writes, and Annie Amplifies. Start with your Reputation & Competitor Report.';
    } else if (location.pathname === '/repupgrade-ai' || location.pathname === '/ai-team/repupgrade-ai') {
      title = 'RepUpgrade.ai — Find Your Next Move | Local Dominate';
      description = 'Understand the customer words behind your reputation and explore your next action. Start with your report.';
    } else if (location.pathname === '/how-it-works') {
      title = 'How Local Dominate Works | RepUpgrade';
      description = 'Start with your report, connect your business context, and put your customers’ words to work.';
    } else if (location.pathname === '/playground') {
      title = 'Explore the Local Dominate Demo | RepUpgrade';
      description = 'Explore a fictional customer review becoming direction, a reply, an article idea, and a social draft.';
    } else if (location.pathname === '/create-access') {
      title = 'Get Upgraded — Your Reputation & Competitor Report';
      description = 'Find your business and request your free Reputation & Competitor Report.';
    } else if (location.pathname === '/agency-how-it-works') {
      title = 'How RepUpgrade Works for Agencies';
      description = 'See how customer words become review replies, article ideas, and social posts—with your agency’s judgment and approval.';
    } else if (location.pathname === '/repupgrade-in-action') {
      title = 'From Customer Words to Marketing | RepUpgrade';
      description = 'Follow an illustrative customer review into a reply draft, article idea, and social post.';
    } else if (location.pathname === '/resources') {
      title = 'Resources & AI Mastermind | RepUpgrade';
      description = 'Explore RepUpgrade, meet the AI Mastermind community, and hear specialist conversations.';
    } else if (location.pathname === '/partners') {
      title = 'Agency Partner Dominate & White Label Partners | RepUpgrade';
      description = 'Compare Agency Partner and White Label Partner economics, responsibilities, and fit for your agency.';
    } else if (location.pathname === '/certified-partner') {
      title = 'Agency Partner Dominate — Qualification & Plans | RepUpgrade';
      description = 'Explore an agency-led RepUpgrade partnership built for established agencies serving local businesses.';
    } else if (location.pathname === '/white-label') {
      title = 'White Label Partner — Your Branded Reputation System | RepUpgrade';
      description = 'See how established agencies can launch a market-facing reputation system using the RepUpgrade licensed foundation.';
    } else if (location.pathname === '/partner-discovery') {
      title = 'Book a RepUpgrade Partner Discovery Call';
      description = 'Discuss customer referrals, Agency Partner Dominate, or White Label Partner on a 30-minute RepUpgrade discovery call.';
    } else if (location.pathname === '/partner-onboarding') {
      title = 'RepUpgrade Partner Onboarding and Kickoff';
      description = 'Complete the private onboarding and kickoff steps after RepUpgrade partner discovery and fit confirmation.';
    } else if (location.pathname === '/partner-access') {
      title = 'RepUpgrade Partner Access';
      description = 'Open the Moxie RepUpgrade Partner Portal after your partner access has been granted.';
    } else if (location.pathname === '/30-day-action-planner') {
      title = '30-Day Reputation Action Planner | RepUpgrade';
      description = 'Turn your local reputation signal into a focused 30-day plan for reviews, responses, customer proof, and trust.';
    } else if (location.pathname.startsWith('/agency-ai-team/')) {
      const agentSlug = location.pathname.split('/').filter(Boolean)[1];
      const agent = getAgencyAgent(agentSlug);

      if (agent) {
        title = `${agent.name} for Agencies | RepUpgrade`;
        description = `${agent.description} See how this capability fits an agency-led, human-supported reputation system.`;
      }
    } else if (location.pathname === '/for-local-businesses' || location.pathname === '/home') {
      title = 'Human-Led AI for Home Services, Coaches & Consultants | RepUpgrade';
      description = 'Human-led AI turns customer feedback into thoughtful review replies, articles, and social content. Start with your free reputation report, then choose your plan.';
    } else if (location.pathname.startsWith('/ai-team/mabel')) {
      title = 'Mabel Writes — Website Article Assistant | RepUpgrade';
      description = 'Mabel turns customer themes into useful article drafts in your business voice. Explore the workflow and start with your report.';
    } else if (location.pathname.startsWith('/ai-team/annie')) {
      title = 'Annie Amplifies — Social Post Assistant | RepUpgrade';
      description = 'Annie helps carry customer praise into social drafts in your voice. Explore the workflow and start with your report.';
    } else if (location.pathname.startsWith('/ai-team/ruby')) {
      title = 'Ruby Replies — Review Reply Assistant | RepUpgrade';
      description = 'Ruby prepares thoughtful review-response drafts in your business voice. Meet Ruby and start with your reputation report.';
    } else if (location.pathname.startsWith('/pricing')) {
      title = 'Local Dominate — Grow, Amplify & Complete Plans | RepUpgrade';
      description = 'Compare Free, Grow at $99/month, Amplify at $199/month, and Complete at $299/month. See your visibility on every plan; run the full strategy with Complete.';
    }

    document.title = title;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    const setMetaContent = (selector, attributeName, attributeValue, content) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const canonicalPath = location.pathname === '/home'
      ? '/for-local-businesses'
      : location.pathname;
    const canonicalUrl = `${window.location.origin}${canonicalPath}`;
    const robotsContent = location.pathname === '/report-thank-you' || location.pathname === '/partner-access' || (location.pathname.startsWith('/plans/') && !getLocalProduct(location.pathname.split('/')[2]))
      ? 'noindex, nofollow'
      : 'index, follow';

    setMetaContent('meta[property="og:title"]', 'property', 'og:title', title);
    setMetaContent('meta[property="og:description"]', 'property', 'og:description', description);
    setMetaContent('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMetaContent('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMetaContent('meta[name="robots"]', 'name', 'robots', robotsContent);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
  }, [location.pathname]);

  return null;
}

function CtaFlowGuard() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const skipPaths = [
      '/plans/',
      '/create-access',
      '/repupgrade-in-action',
      '/repupgrade-ai',
      '/ai-team',
      '/agency-ai-team',
      '/agencies',
      '/partner-fit',
      '/partners',
      '/agency-how-it-works',
      '/resources',
      '/partner-onboarding',
      '/partner-access',
      '/partner-discovery',
      '/certified-partner',
      '/white-label',
    ];
    if (
      location.pathname === '/' ||
      skipPaths.some(path => location.pathname.startsWith(path))
    ) {
      return;
    }

    const replaceCtaText = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          let nextText = child.textContent;
          LEGACY_CTA_LABELS.forEach((label) => {
            nextText = nextText.replace(label, 'Get Upgraded');
          });

          if (nextText !== child.textContent) {
            child.textContent = nextText;
          }

          return;
        }

        replaceCtaText(child);
      });
    };

    const normalizeCtas = () => {
      document.querySelectorAll('a, button').forEach((element) => {
        const label = element.textContent.replace(/\s+/g, ' ').trim();
        const isLegacyCta = LEGACY_CTA_LABELS.some((ctaLabel) => label.includes(ctaLabel));

        if (!isLegacyCta) return;

        replaceCtaText(element);
        element.setAttribute('aria-label', 'Get Upgraded');
        element.dataset.primaryCtaGuard = 'true';

        if (element.tagName.toLowerCase() === 'a') {
          element.setAttribute('href', PRIMARY_CTA_PATH);
        }
      });
    };

    const handlePrimaryCtaClick = (event) => {
      const trigger = event.target.closest('a, button');
      if (!trigger) return;

      const label = trigger.textContent.replace(/\s+/g, ' ').trim();
      const isGuardedCta = trigger.dataset.primaryCtaGuard === 'true';
      const isLegacyCta = LEGACY_CTA_LABELS.some((ctaLabel) => label.includes(ctaLabel));

      if (!isGuardedCta && !isLegacyCta) return;

      event.preventDefault();
      navigate(PRIMARY_CTA_PATH);
    };

    normalizeCtas();

    const observer = new MutationObserver(normalizeCtas);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    document.addEventListener('click', handlePrimaryCtaClick, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handlePrimaryCtaClick, true);
    };
  }, [location.pathname, navigate]);

  return null;
}

function LegacyDiscoveryRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/partner-discovery', search: location.search, hash: location.hash }} replace />;
}

function App() {
  return (
    <Router>
      <ScrollToHash />
      <RouteMetaManager />
      <CtaFlowGuard />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<ChoosePath />} />
        <Route path="/quick-start" element={<QuickStart />} />
        <Route path="/gbp-optimization" element={<GbpOptimization />} />
        <Route path="/agency-how-it-works" element={<AgencyHowItWorks />} />
        <Route path="/resources" element={<AgencyResources />} />
        <Route path="/agencies" element={<Agencies />} />
        <Route path="/partner-apply" element={<PartnerApply />} />
        <Route path="/partner-fit" element={<PartnerFit />} />
        <Route path="/certified-partner" element={<CertifiedPartner />} />
        <Route path="/white-label" element={<WhiteLabelPartner />} />
        <Route path="/partner-discovery" element={<PartnerDiscovery />} />
        <Route path="/discovery" element={<LegacyDiscoveryRedirect />} />
        <Route path="/partner-onboarding" element={<PartnerOnboarding />} />
        <Route path="/partner-access" element={<PartnerAccess />} />
        <Route path="/agency-ai-team/:agentSlug" element={<AgencyAgentDetail />} />
        <Route path="/for-local-businesses" element={<LocalDominate />} />
        <Route path="/plans/:productSlug" element={<LocalProduct />} />
        <Route path="/home" element={<Navigate to="/for-local-businesses" replace />} />
        <Route path="/how-it-works/agent-team" element={<Navigate to="/repupgrade-ai" replace />} />
        <Route path="/repupgrade-ai" element={<LocalOverview />} />

        <Route path="/blog" element={<Blog />} />
        <Route path="/start" element={<Navigate to="/create-access" replace />} />
        <Route path="/how-it-works" element={<LocalOverview howItWorks />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/partners" element={<PricingPage partnersOnly />} />
        <Route path="/download" element={<Download />} />
        <Route path="/playground" element={<LocalDemo />} />
        <Route path="/playground/features" element={<PlaygroundFeatures />} />
        <Route path="/features/local-trust-engine" element={<LocalTrustEngine />} />
        <Route path="/features" element={<Navigate to="/playground/features" replace />} />
        <Route path="/featuers" element={<Navigate to="/playground/features" replace />} />
        <Route path="/playground/case-studies" element={<CaseStudiesPage />} />
        <Route path="/playground/case-study" element={<Navigate to="/playground/case-studies" replace />} />
        <Route path="/case-studies" element={<Navigate to="/playground/case-studies" replace />} />
        <Route path="/local-proof" element={<Navigate to="/playground/case-studies" replace />} />
        <Route path="/vibe-code" element={<VibeCode />} />
        <Route path="/create-access" element={<LocalReport />} />
        <Route path="/report-thank-you" element={<ReportThankYou />} />
        <Route path="/repupgrade-in-action" element={<RepUpgradeInAction />} />
        <Route path="/30-day-action-planner" element={<ActionPlanner />} />

        {/* Legacy funnel routes forward to access */}
        <Route path="/choose-path" element={<Navigate to="/create-access" replace />} />
        <Route path="/starter-boost" element={<Navigate to="/create-access" replace />} />
        <Route path="/starter-boost-success" element={<Navigate to="/create-access" replace />} />
        <Route path="/portal/starter-boost" element={<Navigate to="/create-access" replace />} />
        <Route path="/auth" element={<Navigate to="/create-access" replace />} />
        <Route path="/get-report" element={<Navigate to="/create-access" replace />} />

        {/* Local AI team and report-first product routes */}
        <Route path="/ai-team" element={<LocalAgentsPage />} />
        <Route path="/demo" element={<Navigate to="/playground" replace />} />
        <Route path="/ai-team/repupgrade-ai" element={<LocalOverview />} />
        <Route path="/ai-team/ruby" element={<LocalAgentDetail agentSlug="ruby" />} />
        <Route path="/ai-team/annie" element={<LocalAgentDetail agentSlug="annie" />} />
        <Route path="/ai-team/mabel" element={<LocalAgentDetail agentSlug="mabel" />} />

        {/* Legal Routes */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/dpa" element={<DataProcessingAgreement />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <FloatingSetupButton />
    </Router>
  );
}

export default App;